import moment from "moment";
import { ModalGeneric } from "@/components/utils/ModalGeneric";
import NotasFiscaisService from "@/services/NotasFiscaisService";
import { useEffect } from "react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  nota: any;
  onReenviar?: () => void;
  onUpdateNota?: (patch: any) => void;
};

type NotaErro = {
  codigo?: string;
  mensagem?: string;
  correcao?: string;
};

export default function ViewNfseDetail({
  isOpen,
  onClose,
  nota,
  onReenviar,
  onUpdateNota,
}: Props) {
  if (!isOpen || !nota) return null;

  const status = String(nota?.status ?? "");
  const statusLower = status.toLowerCase();

  const statusClasses =
    statusLower.includes("process") || statusLower.includes("carreg")
      ? "bg-yellow-100 text-yellow-700"
      : statusLower.includes("emit") || statusLower.includes("enviad")
        ? "bg-green-100 text-green-700"
        : statusLower.includes("cancel") || statusLower.includes("neg")
          ? "bg-red-100 text-red-700"
          : "bg-gray-100 text-gray-700";

  const valor = Number(nota?.valor ?? 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

  const emitente = nota?.empresa;
  const tomador = nota?.cliente;

  const nomeEmitente = emitente?.nome_fantasia || emitente?.razao_social || "-";

  const nomeTomador =
    tomador?.nome_completo || tomador?.name_fantasy || tomador?.nome || "-";

  const docTomador = tomador?.cnpj
    ? `CNPJ: ${tomador?.cnpj}`
    : `CPF: ${tomador?.cpf ?? "-"}`;

  function parseErrors(errors_nota: unknown): NotaErro[] {
    if (!errors_nota) return [];

    try {
      if (Array.isArray(errors_nota)) return errors_nota as NotaErro[];

      if (typeof errors_nota === "string") {
        const trimmed = errors_nota.trim();
        if (!trimmed) return [];

        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? (parsed as NotaErro[]) : [];
      }

      if (typeof errors_nota === "object") return [errors_nota as NotaErro];

      return [];
    } catch {
      return [
        {
          codigo: "ERRO_PARSE",
          mensagem: "Não foi possível ler os detalhes do erro da nota.",
          correcao: "Verifique o conteúdo de errors_nota retornado pela API.",
        },
      ];
    }
  }

  const erros = parseErrors(nota?.errors_nota);

  const temErro =
    erros.length > 0 ||
    statusLower.includes("erro") ||
    statusLower.includes("reje") ||
    statusLower.includes("neg") ||
    statusLower.includes("falha");

  const isCancelada = statusLower.includes("cancel");
  const podeReenviar = temErro && !isCancelada;

  useEffect(() => {
    if (!isOpen) return;

    const id_desk = nota?.id_desk ?? nota?.id_desk;

    if (!id_desk) return;

    let alive = true;

    (async () => {
      const res = await NotasFiscaisService.consultarNota({ id_desk });
      if (!alive) return;

      if (!res || res.status !== 200) return;

      if (res.data?.detalhes?.codigo === "nao_encontrado") return;

      const payload = res.data;

      onUpdateNota?.({
        status: payload?.status,
        url: payload?.url,
        data_emissao: payload?.data_emissao,
        errors_nota: payload?.erros ? JSON.stringify(payload.erros) : undefined,
        numero: payload?.numero,
        codigo_verificacao: payload?.codigo_verificacao,
        ref: payload?.ref,
      });
    })();

    return () => {
      alive = false;
    };
  }, [isOpen]);

  return (
    <ModalGeneric
      onClose={onClose}
      size="max-w-3xl w-full max-h-[90vh] overflow-hidden flex-1 overflow-y-auto space-y-5 pr-2 scrollbar-thin"
    >
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-cinza-800">
              Nota Fiscal de Serviço
            </h2>
            <p className="text-sm text-cinza-500">
              Nº {nota?.numero_nota_fiscal ?? "-"} •{" "}
              {moment(nota?.data_emissao).format("DD/MM/YYYY")}
            </p>
          </div>

          <div className="flex flex-col items-end">
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full ${statusClasses}`}
            >
              {status || "-"}
            </span>

            <p className="mt-2 text-lg font-bold text-cinza-800">{valor}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-2">
            <p className="text-sm font-semibold text-cinza-800">Emitente</p>

            <div className="text-sm text-cinza-700 space-y-1">
              <p className="font-medium">{nomeEmitente}</p>
              <p>CNPJ: {emitente?.cnpj ?? "-"}</p>
              <p>
                {emitente?.endereco ?? "-"}
                {emitente?.numero ? `, ${emitente?.numero}` : ""}
                {emitente?.bairro ? ` - ${emitente?.bairro}` : ""}
              </p>
              <p>
                {emitente?.municipio ?? "-"} / {emitente?.uf ?? "-"}
              </p>
              <p>
                IM: {emitente?.inscricao_municipal ?? "-"} • IE:{" "}
                {emitente?.inscricao_estadual ?? "-"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-2">
            <p className="text-sm font-semibold text-cinza-800">Tomador</p>

            <div className="text-sm text-cinza-700 space-y-1">
              <p className="font-medium">{nomeTomador}</p>
              <p>{docTomador}</p>
              <p>E-mail: {tomador?.email ?? "-"}</p>
              <p>Telefone: {tomador?.telefone ?? "-"}</p>
              <p>Endereço: {tomador?.endereco ?? "-"}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-cinza-100 p-4">
          <p className="text-sm font-semibold text-cinza-800 mb-3">
            Informações da emissão
          </p>

          <div className="grid grid-cols-3 gap-3 text-sm text-cinza-700">
            <div>
              <p className="text-cinza-500">Data de emissão</p>
              <p className="font-medium">
                {moment(nota?.data_emissao).format("DD/MM/YYYY")}
              </p>
            </div>

            <div>
              <p className="text-cinza-500">CNAE</p>
              <p className="font-medium">{emitente?.codigo_cnae ?? "-"}</p>
            </div>

            <div>
              <p className="text-cinza-500">Regime</p>
              <p className="font-medium">
                {emitente?.optante_simples_nacional ? "Simples Nacional" : "—"}
              </p>
            </div>
          </div>
        </div>

        {isCancelada && nota?.cancel_description && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-bold text-red-700">Nota cancelada</p>

            <p className="text-sm text-cinza-800 mt-2">
              <span className="font-semibold">Motivo do cancelamento:</span>{" "}
              {nota.cancel_description}
            </p>
          </div>
        )}

        {temErro && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-red-700">
                  Houve um problema ao emitir a nota
                </p>
                <p className="text-xs text-red-700/80 mt-1">
                  Confira os detalhes abaixo e siga as correções sugeridas.
                </p>
              </div>

              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-red-100 text-red-700">
                {status || "erro"}
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {erros.length > 0 ? (
                erros.map((e, idx) => (
                  <div
                    key={`${e?.codigo ?? "erro"}-${idx}`}
                    className="bg-white rounded-xl border border-red-100 p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-red-700">
                        {e?.codigo ? `Código: ${e.codigo}` : "Erro"}
                      </p>
                    </div>

                    <p className="text-sm text-cinza-800 mt-2">
                      <span className="font-semibold">Mensagem:</span>{" "}
                      {e?.mensagem ?? "-"}
                    </p>

                    <p className="text-sm text-cinza-800 mt-2">
                      <span className="font-semibold">Como corrigir:</span>{" "}
                      {e?.correcao ?? "-"}
                    </p>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl border border-red-100 p-3">
                  <p className="text-sm text-cinza-800 mt-2">
                    <span className="font-semibold">Como corrigir:</span>{" "}
                    verifique as permissões/natureza de operação e tente
                    novamente.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {(podeReenviar || nota?.status === "cancelado") && (
            <button
              type="button"
              onClick={onReenviar}
              className="bg-red-100 text-red-700 px-5 py-2 rounded-full font-semibold hover:opacity-90"
            >
              Reenviar nota
            </button>
          )}

          {nota?.url && (
            <a
              href={nota.url}
              target="_blank"
              rel="noreferrer"
              className="bg-azul-400/20 text-azul-400 px-5 py-2 rounded-full font-semibold hover:opacity-90"
            >
              Abrir nota
            </a>
          )}

          <button
            type="button"
            onClick={onClose}
            className="bg-azul-400 text-white px-6 py-2 rounded-full font-semibold hover:opacity-90"
          >
            Fechar
          </button>
        </div>
      </div>
    </ModalGeneric>
  );
}
